import { BehaviorSubject } from 'rxjs';

import { Maybe } from '@plugins/shared/types/helpers';
import { isNumber } from '@plugins/shared/utils/assertFunctions';

import type {
  AnimationVariants,
  CssBaseAnimationVariables,
  LiteralSizes,
  UnitSizes,
} from '../../public/types';
import {
  ANIMATION_DEFAULTS,
  ANIMATION_PROPS,
  BASE_SIZE,
  BASE_SIZE_PROP,
  DEFAULT_ANIMATION_UNIT,
  ICON_BASE_SIZES,
  UNIT_SIZES,
} from '../constants';

export const isBaseSize = (arg: string | number): arg is LiteralSizes => {
  if (typeof arg === 'number') return false;
  return ICON_BASE_SIZES.some((size) => arg === size);
};

export const isUnitSize = (arg: string | number): arg is UnitSizes => {
  if (typeof arg === 'number') return false;
  return UNIT_SIZES.some((size) => {
    if (!arg.includes(size)) return false;
    const split = arg.split(size);
    if (split.length !== 2) return false;
    const num = Number(split[0]);
    if (Number.isNaN(num) || num === 0) return false;
    return true;
  });
};

export const handleSizeInput = (
  iconSize: Maybe<string | number>,
  sizeSubj: BehaviorSubject<string>,
  hostStyle: CSSStyleDeclaration,
): void => {
  if (iconSize === undefined || iconSize === null) {
    return;
  }
  if (isBaseSize(iconSize)) {
    const currentBase = hostStyle.getPropertyValue(BASE_SIZE_PROP);
    sizeSubj.next(iconSize);
    // Reset the currentBase
    if (currentBase !== BASE_SIZE) {
      hostStyle.setProperty(BASE_SIZE_PROP, BASE_SIZE);
    }
    return;
  }
  if (isUnitSize(iconSize)) {
    sizeSubj.next('');
    hostStyle.setProperty(BASE_SIZE_PROP, iconSize);
  }
  if (isNumber(iconSize) && !Number.isNaN(iconSize) && iconSize !== 0) {
    sizeSubj.next('');
    hostStyle.setProperty(BASE_SIZE_PROP, `${iconSize}px`);
  }
};

export const handleDegInput = (arg: string | number): string => {
  if (isNumber(arg)) {
    if (!Number.isNaN(arg)) return `${arg}deg`;
    return '';
  }
  const trimmedArg = arg.trim();
  const stringifiedDeg = /\d+deg$/,
    stringifiedNum = /^\d+$/;
  if (stringifiedDeg.test(trimmedArg)) return trimmedArg;
  if (stringifiedNum.test(trimmedArg)) return `${trimmedArg}deg`;
  return '';
};

export const defaultCssSetter = (
  hostStyle: CSSStyleDeclaration,
  targetVars: CssBaseAnimationVariables[] = ANIMATION_PROPS,
): void => {
  targetVars.forEach((variable) => {
    hostStyle.setProperty(
      `--animation-${variable}`,
      ANIMATION_DEFAULTS[variable],
    );
  });
};

/** convert camelCase to kebab-case class names. Return animation duration, if applicable */
export const handleAnimationInput = (
  animation: Maybe<AnimationVariants>,
  classSetter: BehaviorSubject<string>,
  hostStyle: CSSStyleDeclaration,
): void => {
  if (!animation) {
    defaultCssSetter(hostStyle);
    classSetter.next('no-animation');
    return;
  }

  const delimiter = '-';

  const split = animation.split(' ').filter((val) => !!val.trim());

  if (split.length === 1) {
    defaultCssSetter(hostStyle);
    classSetter.next(animation);
    return;
  }
  const durationProps = ['duration', 'delay'];
  const presentArgs: string[] = [];

  const animationClass = split[0];

  const animationArgs = split.slice(1).map((val) => {
    const delimiterIndex = val.indexOf(delimiter);
    const variable = val.substring(0, delimiterIndex);
    presentArgs.push(variable);
    return {
      variable,
      value: val.substring(delimiterIndex + 1),
    };
  });

  const missing = presentArgs.length
    ? ANIMATION_PROPS.filter((prop) => !presentArgs.includes(prop))
    : [];

  animationArgs.forEach(({ variable, value }) => {
    let finalVal = value;
    if (durationProps.includes(variable)) {
      // Check if the stringified number variable contains a unit
      // Previous typing constraints should prevent non-numbers to be passed
      finalVal = /\d+m{0,1}s$/.test(finalVal)
        ? finalVal
        : `${finalVal}${DEFAULT_ANIMATION_UNIT}`;
    }
    hostStyle.setProperty(`--animation-${variable}`, finalVal);
  });
  // Reset animation arguments that aren't present anymore
  defaultCssSetter(hostStyle, missing);

  classSetter.next(animationClass);
};
